import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Activity, Users, Sparkles, ChevronRight } from 'lucide-react';
import api from '../utils/api';

const AVXLiveStatsWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const load = async () => {
    try {
      const r = await api.get('/avx/stats/public');
      setStats(r.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) return null;

  return (
    <section className="py-12 px-4" data-testid="avx-live-widget">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-br from-yellow-500/10 via-orange-500/10 to-purple-500/10 border border-yellow-500/30 rounded-2xl p-6 lg:p-8 backdrop-blur-sm">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded-full font-semibold">FANTASY</span>
                <span className="flex items-center gap-1 text-xs text-emerald-400">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  Live
                </span>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold mt-2 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                ANANT Virtual Exchange
              </h2>
              <p className="text-sm text-slate-300 mt-1">Trade virtual assets with up to 33% Affiliate Network commission</p>
            </div>
            <Link
              to="/avx"
              className="px-5 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold rounded-xl flex items-center gap-2 hover:scale-105 transition"
              data-testid="avx-widget-cta"
            >
              <Sparkles className="w-4 h-4" />
              Open AVX
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* 4 Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              icon={<Activity className="w-4 h-4" />}
              label="24h Volume"
              value={`${stats.volume_24h_at.toLocaleString()} AT`}
              color="from-emerald-500/20 to-teal-500/20 border-emerald-500/30"
            />
            <StatCard
              icon={<TrendingUp className="w-4 h-4" />}
              label="24h Trades"
              value={stats.trades_24h}
              color="from-blue-500/20 to-cyan-500/20 border-blue-500/30"
            />
            <StatCard
              icon={<Users className="w-4 h-4" />}
              label="Total Traders"
              value={stats.total_users}
              color="from-purple-500/20 to-pink-500/20 border-purple-500/30"
            />
            <StatCard
              icon={<Sparkles className="w-4 h-4" />}
              label="Active Assets"
              value={stats.active_assets}
              color="from-yellow-500/20 to-amber-500/20 border-yellow-500/30"
            />
          </div>

          {/* Top Assets 24h */}
          {(stats.top_assets_24h || []).length > 0 && (
            <div className="mt-5">
              <div className="text-xs text-slate-400 mb-2">🔥 Top Assets (24h)</div>
              <div className="flex flex-wrap gap-2">
                {stats.top_assets_24h.map((a, i) => (
                  <div
                    key={a.symbol}
                    className="flex items-center gap-2 bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                  >
                    <span className="font-bold text-yellow-300">#{i + 1}</span>
                    <span className="font-semibold">{a.symbol}</span>
                    <span className="text-slate-400">{a.volume.toLocaleString()} AT</span>
                    <span className="text-xs text-slate-500">({a.trades} trades)</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 text-xs text-yellow-200/80">
            ⚠️ Fantasy/Simulation Game — Not a SEBI/RBI registered exchange
          </div>
        </div>
      </div>
    </section>
  );
};

const StatCard = ({ icon, label, value, color }) => (
  <div className={`bg-gradient-to-br ${color} border rounded-xl p-4`}>
    <div className="flex items-center gap-2 text-slate-300 text-xs">
      {icon}
      <span>{label}</span>
    </div>
    <div className="text-2xl font-bold text-white mt-1">{value}</div>
  </div>
);

export default AVXLiveStatsWidget;
